import {
  ORGX_WIZARD_AUTH_PATH,
  ORGX_WIZARD_KEYTAR_ACCOUNT,
  ORGX_WIZARD_KEYTAR_SERVICE,
} from "../constants.js";
import {
  deleteFileIfExists,
  parseJsonObject,
  readTextIfExists,
  writeJsonFile,
} from "./fs.js";

export type WizardAuthStorage = "file" | "keytar";
export type WizardAuthSource = "manual" | "pkce";

export interface WizardAuthRecord {
  apiKey: string;
  keyPrefix: string;
  baseUrl: string;
  source: WizardAuthSource;
  storage: WizardAuthStorage;
  verifiedAt: string;
  /** Present when source is "pkce" — used to silently refresh access_token. */
  refreshToken?: string;
  /** OAuth client_id used to obtain this token (needed for refresh). */
  oauthClientId?: string;
}

export interface WizardAuthMetadata {
  baseUrl: string;
  keyPrefix: string;
  source: WizardAuthSource;
  storage: WizardAuthStorage;
  verifiedAt: string;
}

export interface WizardSecretStore {
  deletePassword(service: string, account: string): Promise<boolean>;
  getPassword(service: string, account: string): Promise<string | null>;
  setPassword(service: string, account: string, password: string): Promise<void>;
}

interface StoredWizardAuthRecord {
  apiKey?: string;
  baseUrl: string;
  keyPrefix: string;
  source?: WizardAuthSource;
  storage: WizardAuthStorage;
  verifiedAt: string;
  refreshToken?: string;
  oauthClientId?: string;
}

interface WizardAuthStoreOptions {
  preferSecureStorage?: boolean;
  secretStore?: WizardSecretStore | null;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export function extractKeyPrefix(apiKey: string): string {
  return apiKey.trim().slice(0, 12);
}

function parseStoredWizardAuthRecord(raw: string | null): StoredWizardAuthRecord | null {
  const parsed = parseJsonObject(raw);

  if (
    !isNonEmptyString(parsed.keyPrefix) ||
    !isNonEmptyString(parsed.baseUrl) ||
    !isNonEmptyString(parsed.verifiedAt)
  ) {
    return null;
  }

  const storage = parsed.storage === "keytar" ? "keytar" : "file";
  const source: WizardAuthSource = parsed.source === "pkce" ? "pkce" : "manual";

  return {
    baseUrl: parsed.baseUrl.trim(),
    ...(isNonEmptyString(parsed.apiKey) ? { apiKey: parsed.apiKey.trim() } : {}),
    keyPrefix: parsed.keyPrefix.trim(),
    source,
    storage,
    verifiedAt: parsed.verifiedAt.trim(),
    ...(isNonEmptyString(parsed.refreshToken) ? { refreshToken: parsed.refreshToken.trim() } : {}),
    ...(isNonEmptyString(parsed.oauthClientId) ? { oauthClientId: parsed.oauthClientId.trim() } : {}),
  };
}

async function loadDefaultSecretStore(): Promise<WizardSecretStore | null> {
  if (process.env.ORGX_WIZARD_DISABLE_KEYTAR === "1") {
    return null;
  }

  try {
    const imported = await import("keytar");
    const candidate = (
      "default" in imported && imported.default ? imported.default : imported
    ) as Partial<WizardSecretStore>;

    if (
      typeof candidate.getPassword === "function" &&
      typeof candidate.setPassword === "function" &&
      typeof candidate.deletePassword === "function"
    ) {
      return candidate as WizardSecretStore;
    }
  } catch {
    return null;
  }

  return null;
}

async function resolveSecretStore(
  options: WizardAuthStoreOptions,
): Promise<WizardSecretStore | null> {
  if (options.secretStore !== undefined) {
    return options.secretStore;
  }

  return loadDefaultSecretStore();
}

function buildStoredRecord(
  value: WizardAuthRecord,
): StoredWizardAuthRecord {
  return {
    ...(value.storage === "file" ? { apiKey: value.apiKey } : {}),
    baseUrl: value.baseUrl,
    keyPrefix: value.keyPrefix,
    source: value.source,
    storage: value.storage,
    verifiedAt: value.verifiedAt,
    ...(value.refreshToken ? { refreshToken: value.refreshToken } : {}),
    ...(value.oauthClientId ? { oauthClientId: value.oauthClientId } : {}),
  };
}

export function readWizardAuthMetadata(
  authPath: string = ORGX_WIZARD_AUTH_PATH,
): WizardAuthMetadata | null {
  const stored = parseStoredWizardAuthRecord(readTextIfExists(authPath));
  if (!stored) {
    return null;
  }

  return {
    baseUrl: stored.baseUrl,
    keyPrefix: stored.keyPrefix,
    source: stored.source ?? "manual",
    storage: stored.storage,
    verifiedAt: stored.verifiedAt,
  };
}

export async function readWizardAuth(
  authPath: string = ORGX_WIZARD_AUTH_PATH,
  options: WizardAuthStoreOptions = {},
): Promise<WizardAuthRecord | null> {
  const stored = parseStoredWizardAuthRecord(readTextIfExists(authPath));
  if (!stored) {
    return null;
  }

  const source: WizardAuthSource = stored.source ?? "manual";

  const apiKeyFromFile =
    stored.storage === "file" && isNonEmptyString(stored.apiKey)
      ? stored.apiKey.trim()
      : null;

  if (apiKeyFromFile) {
    return {
      apiKey: apiKeyFromFile,
      baseUrl: stored.baseUrl,
      keyPrefix: stored.keyPrefix,
      source,
      storage: "file",
      verifiedAt: stored.verifiedAt,
      ...(stored.refreshToken ? { refreshToken: stored.refreshToken } : {}),
      ...(stored.oauthClientId ? { oauthClientId: stored.oauthClientId } : {}),
    };
  }

  if (stored.storage !== "keytar") {
    return null;
  }

  const secretStore = await resolveSecretStore(options);
  if (!secretStore) {
    return null;
  }

  let apiKey: string | null;
  try {
    apiKey = await secretStore.getPassword(
      ORGX_WIZARD_KEYTAR_SERVICE,
      ORGX_WIZARD_KEYTAR_ACCOUNT,
    );
  } catch {
    // An unavailable OS secret store is equivalent to a missing credential.
    // Preserve its metadata and let the resolver try its configured fallback.
    return null;
  }

  if (!isNonEmptyString(apiKey)) {
    return null;
  }

  return {
    apiKey: apiKey.trim(),
    baseUrl: stored.baseUrl,
    keyPrefix: stored.keyPrefix,
    source,
    storage: "keytar",
    verifiedAt: stored.verifiedAt,
    ...(stored.refreshToken ? { refreshToken: stored.refreshToken } : {}),
    ...(stored.oauthClientId ? { oauthClientId: stored.oauthClientId } : {}),
  };
}

export async function writeWizardAuth(
  value: {
    apiKey: string;
    baseUrl: string;
    keyPrefix?: string;
    verifiedAt: string;
    source?: WizardAuthSource;
    refreshToken?: string;
    oauthClientId?: string;
  },
  authPath: string = ORGX_WIZARD_AUTH_PATH,
  options: WizardAuthStoreOptions = {},
): Promise<WizardAuthRecord> {
  const secretStore = options.preferSecureStorage === false
    ? null
    : await resolveSecretStore(options);

  const record: WizardAuthRecord = {
    apiKey: value.apiKey.trim(),
    keyPrefix: value.keyPrefix?.trim() || extractKeyPrefix(value.apiKey),
    baseUrl: value.baseUrl.trim(),
    source: value.source ?? "manual",
    storage: secretStore ? "keytar" : "file",
    verifiedAt: value.verifiedAt.trim(),
    ...(value.refreshToken ? { refreshToken: value.refreshToken } : {}),
    ...(value.oauthClientId ? { oauthClientId: value.oauthClientId } : {}),
  };

  if (secretStore) {
    await secretStore.setPassword(
      ORGX_WIZARD_KEYTAR_SERVICE,
      ORGX_WIZARD_KEYTAR_ACCOUNT,
      record.apiKey,
    );
  }

  writeJsonFile(authPath, buildStoredRecord(record), { mode: 0o600 });
  return record;
}

export async function clearWizardAuth(
  authPath: string = ORGX_WIZARD_AUTH_PATH,
  options: WizardAuthStoreOptions = {},
): Promise<boolean> {
  const secretStore = await resolveSecretStore(options);
  const secretRemoved = secretStore
    ? await secretStore.deletePassword(
        ORGX_WIZARD_KEYTAR_SERVICE,
        ORGX_WIZARD_KEYTAR_ACCOUNT,
      )
    : false;
  const fileRemoved = deleteFileIfExists(authPath);
  return secretRemoved || fileRemoved;
}
